import React, { createContext, useContext, useEffect, useState } from "react";
import { User as FirebaseUser } from "firebase/auth";
import { signInWithGoogle, signOut, onAuthStateChange } from "../services/firebase";
import { getFirestore, doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { useOptionalApp } from "./AppProvider";
import { User } from "../types";
import { getOrgFromUrl } from "../utils/urlToOrg";

interface AuthContextType {
  firebaseUser: FirebaseUser | null;
  appUser: User | null;
  loading: boolean;
  signIn: () => Promise<void>;
  logout: () => Promise<void>;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);
const db = getFirestore();

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const appContext = useOptionalApp();
  const reload = appContext?.reload;

  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [appUser, setAppUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // --------------------------------------------------------
  // Firebase Auth Listener
  // --------------------------------------------------------

  useEffect(() => {
    const unsubscribe = onAuthStateChange(async (user) => {
      setFirebaseUser(user);

      if (!user) {
        setAppUser(null);
        setLoading(false);
        return;
      }

      await handleLogin(user);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // --------------------------------------------------------
  // Login Flow
  // --------------------------------------------------------

  const handleLogin = async (firebaseUser: FirebaseUser) => {
    const email = firebaseUser.email?.toLowerCase().trim();

    if (!email) {
      await signOut();
      alert("Authentication requires an email.");
      return;
    }

    console.log("[Auth] Logging in:", email);

    try {
      const userRef = doc(db, "users", email);
      const snap = await getDoc(userRef);

      if (!snap.exists()) {
        await signOut();
        alert(`Access denied: No profile for ${email}`);
        return;
      }

      const profile = snap.data();

      // update login metadata
      await setDoc(
        userRef,
        {
          name: firebaseUser.displayName || profile.name,
          avatar: firebaseUser.photoURL || profile.avatar,
          lastLogin: serverTimestamp()
        },
        { merge: true }
      );

      const newUser: User = {
        id: userRef.id,
        email,
        name: firebaseUser.displayName || profile.name || "User",
        avatar: firebaseUser.photoURL || profile.avatar,
        phoneNumber: profile.phoneNumber,
        linkedinUrl: profile.linkedinUrl,
        githubUrl: profile.githubUrl,
        facebookUrl: profile.facebookUrl,
        role: profile.role || "developer",
        organizations: profile.organizations || [],
        projects: profile.projects || [],
        organizationId: getOrgFromUrl() || profile.organizationId || "",
        permissions: profile.permissions || {}
      };

      setAppUser(newUser);

      // Load application data when AppProvider context is available.
      if (reload) {
        await reload(email);
      } else {
        console.warn("[Auth] AppProvider context unavailable, skipping app state reload");
      }

      console.log("[Auth] Login complete:", newUser.name);

    } catch (err) {
      console.error("[Auth] Login error:", err);
      alert("Failed to authenticate user.");
    }
  };

  // --------------------------------------------------------
  // Sign In
  // --------------------------------------------------------

  const signIn = async () => {
    try {
      setLoading(true);
      await signInWithGoogle();
    } catch (err) {
      console.error("[Auth] Sign-in failed:", err);
      setLoading(false);
      throw err;
    }
  };

  // --------------------------------------------------------
  // Logout
  // --------------------------------------------------------

  const logout = async () => {
    try {
      await signOut();
      setAppUser(null);
    } catch (err) {
      console.error("[Auth] Logout failed:", err);
      throw err;
    }
  };

  const value: AuthContextType = {
    firebaseUser,
    appUser,
    loading,
    signIn,
    logout,
    isAuthenticated: !!firebaseUser && !!appUser
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// --------------------------------------------------------
// Hook
// --------------------------------------------------------

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return ctx;
}
